<script setup lang="ts">
import WidgetContainer from "@/components/EditView/widgets/WidgetContainer.vue";
import {WidgetProps} from "@/utils/presenters/interfaces.ts";
import FieldWidgetUtil from "@/utils/editView/widgets/FieldWidgetUtil.ts";
import ErrorTooltip from "@/components/EditView/ErrorTooltip.vue";
import ExtraCodeFieldWidget from "@/components/EditView/widgets/ExtraCodeFieldWidget.vue";
import SuggestionProviderWidget from "@/components/EditView/widgets/SuggestionProviderWidget.vue";
import {ref} from "vue";
import {QInput} from "quasar";
import {focusAndSelectInput, NamaVueWidget} from "@/utils/common/NamaVueWidget.ts";
import SearcherViewPresenter from "@/utils/common/SearcherViewPresenter.ts";
import NamaIcon from "@/components/icons/NamaIcon.vue";

const props = defineProps<WidgetProps>();
const fieldId = props.field.id!;
let fieldUtil = new FieldWidgetUtil(props.presenter, props.fieldMetaData);
let text = fieldUtil.formattedValue();
let emit = defineEmits(['navigateToNext']);

let customPlaceHolder = props.customPlaceHolder;
const input = ref<QInput>()
const extraCodeInput = ref<NamaVueWidget>()
const suggestionInput = ref<NamaVueWidget>()

function focus() {
  if (input.value) {
    focusAndSelectInput(input);
  } else if (extraCodeInput.value?.focus) {
    extraCodeInput.value.focus();
  } else if (suggestionInput.value?.focus) {
    suggestionInput.value.focus();
  }
}

function canReceiveFocus() {
  return !fieldUtil.isFieldDisabled(props.forceEnableField);
}

function onEnterClicked(e: KeyboardEvent, doNotGoToNextWithEnter?: boolean) {
  if (props.presenter instanceof SearcherViewPresenter) {
    fieldUtil.onBlur();
    props.presenter.onRefreshClicked();
  }
  if (doNotGoToNextWithEnter)
    return;
  if (emit)
    emit('navigateToNext', {keyboardEvent: e})
}

defineExpose({focus, canReceiveFocus})
</script>

<template>
  <WidgetContainer
    :field="field"
    :entityType="presenter.entityType"
    :isSpanned="isSpanned" :isFieldDisabled="fieldUtil.isFieldDisabled(forceEnableField)"
    :isFieldRequired="fieldUtil.isFieldRequired()" :doNotApplyThemeLayouts="doNotApplyThemeLayouts"
    :isInsideComposite="isInsideComposite">
    <template #icon>
      <NamaIcon :block="field" :presenter="presenter" class="tw-size-[22px] tw-text-[22px]"/>
    </template>
    <template v-slot:widget="{ showAsPassword, disableSuggestionProvider, doNotGoToNextWithEnter }">
      <ExtraCodeFieldWidget v-if="fieldUtil.isCodeFieldOrExtraCodeField() && !disableSuggestionProvider" :field="field"
                            :isInsideComposite="isInsideComposite" :fieldMetaData="fieldMetaData" :presenter="presenter"
                            :doNotApplyThemeLayouts="doNotApplyThemeLayouts" :showAsPassword="showAsPassword" :forceEnableField="forceEnableField"
                            @navigateToNext="(args)=>{ if (!doNotGoToNextWithEnter) $emit('navigateToNext',args)}" ref="extraCodeInput"/>
      <SuggestionProviderWidget v-else-if="fieldUtil.isSuggestionProviderField && !disableSuggestionProvider" :field="field"
                                :isInsideComposite="isInsideComposite" :fieldMetaData="fieldMetaData" :presenter="presenter"
                                :doNotApplyThemeLayouts="doNotApplyThemeLayouts" :showAsPassword="showAsPassword" :forceEnableField="forceEnableField"
                                @navigateToNext="(args)=>{ if (!doNotGoToNextWithEnter) $emit('navigateToNext',args)}" ref="suggestionInput"/>
      <q-input v-else outlined dense hide-bottom-space
               :readonly="fieldUtil.isFieldDisabled(forceEnableField)"
               :id="fieldId" ref="input"
               :error="!!props.presenter.fetchFieldErrorMsg(fieldId)"
               :placeholder="customPlaceHolder?customPlaceHolder:''"
               autocomplete="off"
               :type="showAsPassword ? 'password' : 'text'"
               class="nama-customize-dense-input nama-focused-input"
               :class="{'nama-field-input-disabled':fieldUtil.isFieldDisabled(forceEnableField),'nama-field-input':!fieldUtil.isFieldDisabled(forceEnableField)}"
               input-class="nama-input-class"
               :model-value="text" @update:model-value="fieldUtil.onPartialUserInput" @focus="fieldUtil.onFocus" @blur="fieldUtil.onBlur"
               @keydown.enter="(e) => onEnterClicked(e, doNotGoToNextWithEnter)">
        <ErrorTooltip v-if="presenter.fetchFieldErrorMsg(fieldId)" :error-msg="presenter.fetchFieldErrorMsg(fieldId)"/>
      </q-input>
    </template>
  </WidgetContainer>
</template>

<style>

</style>
